import React from 'react';
import { OneWayInOpportunitySimpleV2Dto } from 'src/api/v2';
import { useUsers } from 'src/features/users';
import { useTranslation } from 'react-i18next';
import { FaIcon, FaIcons } from '@innovationnorway/component-library';
import { Link } from 'react-router-dom';
import { ONE_WAY_IN_DETAILS } from '../../opportunity-details/routes';
import useGeoNorge from '../../hooks/use-geonorge';
import StatusDot from '../../opportunity-details/components/StatusDot/StatusDot';
import { icon } from '@fortawesome/fontawesome-svg-core';
import { hoverEnter, hoverLeave, dragEvent } from '../helpers';
import { Tooltip } from '@innovationnorway/cl-react';

const OpportunityCard = ({
  opportunity,
  onAddAdvisorModalOpen,
  onRecommendServicesModalOpen,
  onDeliverServicesModalOpen,
}: {
  opportunity: OneWayInOpportunitySimpleV2Dto;
  onAddAdvisorModalOpen: (opportunity: OneWayInOpportunitySimpleV2Dto) => void;
  onRecommendServicesModalOpen: (opportunity: OneWayInOpportunitySimpleV2Dto) => void;
  onDeliverServicesModalOpen: (opportunity: OneWayInOpportunitySimpleV2Dto) => void;
}) => {
  const { t: tCommon } = useTranslation('common');
  const { t: tOneWayIn } = useTranslation('oneWayIn');

  const { getMunicipalityNameByNumber } = useGeoNorge();
  const { users } = useUsers();

  const { advisorInitials, advisorName } = React.useMemo(() => {
    const advisor = users.find((u) => u.id === opportunity?.assignedTo);
    if (!opportunity?.assignedTo || !advisor) {
      return { advisorInitials: undefined, advisorName: undefined };
    }
    return {
      advisorInitials: `${advisor?.firstName[0].toUpperCase()}${advisor?.lastName[0].toUpperCase()}`,
      advisorName: `${advisor?.firstName} ${advisor?.lastName}`,
    };
  }, [opportunity?.assignedTo, users]);

  const advisorColorClass = React.useMemo(() => {
    if (!advisorInitials) {
      return '';
    }
    const sortedUsers = users.sort(
      (a, b) => new Date(a.createdOn).getTime() - new Date(b.createdOn).getTime(),
    );
    const userIndex = sortedUsers.findIndex((u) => u.id === opportunity?.assignedTo);
    return `background-color--${(userIndex % 7) + 1}`;
  }, [advisorInitials, opportunity?.assignedTo, users]);

  return (
    <div
      onDragEnter={(e) => {
        hoverEnter(e);
      }}
      onDragLeave={(e) => {
        hoverLeave(e);
      }}
      onDragStart={(e) => dragEvent(e, opportunity)}
      draggable
      className="board__card stack-medium"
      key={opportunity.id}
    >
      <Link
        to={ONE_WAY_IN_DETAILS.BASE(opportunity.oneWayInId)}
        draggable={false}
        className="stack-medium text-decoration__none"
      >
        <div className="flex gap-x--2 gap-y--1">
          {opportunity.organizationName && (
            <div className="icon">
              <FaIcon name={FaIcons.IdCardClipLight} />
              <div className="icon-content">{opportunity.customerName}</div>
            </div>
          )}
          <div className="icon">
            <FaIcon name={opportunity.organizationName ? FaIcons.BuildingLight : FaIcons.IdCardClipLight} />

            <div className="icon-content">{opportunity.organizationName || opportunity.customerName}</div>
          </div>
          <div className="icon">
            <FaIcon name={FaIcons.MapMarkerAltLight} />{' '}
            <div className="icon-content">{getMunicipalityNameByNumber(opportunity.municipality)}</div>
          </div>
        </div>
        <div className="display--flex gap--1 align-items--baseline">
          <StatusDot opportunity={opportunity} />
          <h2 className="heading">{opportunity.title}</h2>
        </div>
        <div className="flex gap--1">
          <div className="stack-small">
            <div className="date">
              {opportunity.submittedOn && (
                <span className="text-decoration--none border-right padding-x--1">
                  {tCommon('delivered')}: {new Date(opportunity.submittedOn).toLocaleDateString()}
                </span>
              )}
            </div>
          </div>
          <div className="date">
            <span>
              {tCommon('lastChanged')}:{' '}
              {opportunity.lastChangedOn && new Date(opportunity.lastChangedOn).toLocaleDateString()}
            </span>
          </div>
        </div>
      </Link>

      <div className="display--flex align-items--center gap--2">
        <Tooltip content={tOneWayIn('button.RecommendService')}>
          <button
            className="action-button--container"
            onClick={() => onRecommendServicesModalOpen(opportunity)}
          >
            <FaIcon icon={icon({ prefix: 'fal', iconName: 'hand-holding-box' })} />
          </button>
        </Tooltip>
        <Tooltip content={tOneWayIn('customerFollowUp.deliverService')}>
          <button
            className="action-button--container"
            onClick={() => onDeliverServicesModalOpen(opportunity)}
          >
            <FaIcon icon={icon({ prefix: 'fal', iconName: 'grid-2-plus' })} />
          </button>
        </Tooltip>
        <Tooltip
          content={
            advisorName
              ? `${tOneWayIn('tooltip.changeAdvisor')} (${advisorName})`
              : tOneWayIn('tooltip.addAdvisor')
          }
        >
          <button
            className={`action-button--container ${advisorColorClass} ${advisorInitials ? 'no-border' : ''}`}
            onClick={() => onAddAdvisorModalOpen(opportunity)}
          >
            {advisorInitials ? (
              <span className="color--white">{advisorInitials}</span>
            ) : (
              <FaIcon icon={icon({ prefix: 'fal', iconName: 'user-plus' })} />
            )}
          </button>
        </Tooltip>
      </div>
    </div>
  );
};

export default OpportunityCard;
